// src/pages/Customer/ActiveOrders.jsx - SUPER OPTIMIZED

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ChefHat, Package, ArrowLeft, ShoppingBag } from 'lucide-react';
import StorageService from '../../services/StorageService';

const ActiveOrders = ({ restaurantId, tableId, onBack }) => {
  const [activeOrders, setActiveOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ OPTIMIZED: Load active orders with caching
  const loadActiveOrders = useCallback(async () => {
    try {
      // ✅ Use cache for faster loads
      const data = await StorageService.getData();
      const restaurant = data.restaurants.find(r => r.id === restaurantId);
      
      if (restaurant && tableId) {
        const orders = restaurant.orders
          .filter(order => order.tableId === tableId && order.status !== 'Delivered')
          .sort((a, b) => new Date(b.time) - new Date(a.time));
        
        setActiveOrders(orders);
      }
    } catch (error) {
      console.error('❌ Error loading active orders:', error);
    } finally {
      if (loading) {
        setLoading(false);
      }
    }
  }, [restaurantId, tableId, loading]);

  // ✅ OPTIMIZED: Initial load and smart polling
  useEffect(() => {
    loadActiveOrders();
    
    // ✅ Poll every 3 seconds instead of constantly
    const interval = setInterval(loadActiveOrders, 3000);
    return () => clearInterval(interval);
  }, [loadActiveOrders]);

  // ✅ MEMOIZED: Toggle selected order
  const toggleOrderDetails = useCallback((orderId) => {
    setSelectedOrderId(prev => prev === orderId ? null : orderId);
  }, []);

  // ✅ MEMOIZED: Get status color
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Preparing': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Ready': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }, []);

  // ✅ MEMOIZED: Get status icon
  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'Pending': return Clock;
      case 'Preparing': return ChefHat;
      case 'Ready': return Package;
      default: return ShoppingBag;
    }
  }, []);

  // ✅ MEMOIZED: Has active orders
  const hasActiveOrders = useMemo(() => activeOrders.length > 0, [activeOrders.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <ChefHat className="w-16 h-16 text-orange-500 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 shadow-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={onBack}
            className="mb-4 text-white hover:text-orange-100 flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" /> Back to Menu
          </button>
          <h1 className="text-3xl font-bold mb-2">Active Orders</h1>
          <p className="text-orange-100">Track all your table orders in real-time</p>
          {hasActiveOrders && (
            <p className="text-orange-200 text-sm mt-1">
              {activeOrders.length} active order{activeOrders.length > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {hasActiveOrders ? (
          <div className="space-y-4">
            {activeOrders.map((order, index) => (
              <OrderCard
                key={order.id}
                order={order}
                index={index}
                isExpanded={selectedOrderId === order.id}
                onToggle={toggleOrderDetails}
                getStatusColor={getStatusColor}
                getStatusIcon={getStatusIcon}
              />
            ))}
          </div>
        ) : (
          <EmptyOrders onBack={onBack} />
        )}

        {/* Auto Refresh Notice */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            🔄 This page automatically refreshes every 3 seconds
          </p>
        </div>
      </div>
    </div>
  );
};

// ✅ OPTIMIZED: Separate OrderCard component
const OrderCard = React.memo(({ 
  order, 
  index, 
  isExpanded, 
  onToggle, 
  getStatusColor, 
  getStatusIcon 
}) => {
  const StatusIcon = getStatusIcon(order.status);

  // ✅ MEMOIZED: Calculate remaining time
  const remainingTime = useMemo(() => {
    if (!order.preparationStartTime || !order.preparationTime) return null;
    
    const startTime = new Date(order.preparationStartTime).getTime();
    const now = Date.now();
    const elapsed = Math.floor((now - startTime) / 1000);
    const prepTimeInSeconds = order.preparationTime * 60;
    const remaining = Math.max(0, prepTimeInSeconds - elapsed);
    
    const mins = Math.floor(remaining / 60);
    const secs = remaining % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, [order.preparationStartTime, order.preparationTime]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
    >
      <div className="p-6">
        {/* Order Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-1">
              Order #{order.id}
            </h3>
            <p className="text-sm text-gray-600">
              {new Date(order.time).toLocaleString()}
            </p>
            <p className="text-sm text-gray-700 font-medium mt-1">
              {order.customerName}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-orange-500 mb-2">
              ₹{order.total}
            </p>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold border-2 flex items-center gap-2 ${getStatusColor(order.status)}`}>
              <StatusIcon className="w-5 h-5" />
              {order.status}
            </span>
          </div>
        </div>

        {/* Status Messages */}
        <StatusMessage 
          order={order} 
          remainingTime={remainingTime} 
        />

        {/* Order Items Toggle */}
        <div className="border-t pt-4">
          <button
            onClick={() => onToggle(order.id)}
            className="w-full text-left flex justify-between items-center mb-3 hover:bg-gray-50 p-2 rounded-lg transition-colors"
          >
            <h4 className="font-semibold text-gray-700">
              Order Items ({order.itemDetails?.length || 0})
            </h4>
            <span className="text-orange-500 text-sm font-medium">
              {isExpanded ? 'Hide' : 'Show'} Details
            </span>
          </button>
          
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="space-y-2"
              >
                {order.itemDetails?.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-800">{item.name}</p>
                      <p className="text-sm text-gray-600">
                        ₹{item.price} × {item.quantity}
                      </p>
                    </div>
                    <p className="font-bold text-orange-600">
                      ₹{item.price * item.quantity}
                    </p>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
});

OrderCard.displayName = 'OrderCard';

// ✅ OPTIMIZED: Separate StatusMessage component
const StatusMessage = React.memo(({ order, remainingTime }) => {
  // Preparing with time
  if (order.preparationTime && order.status === 'Preparing') {
    return (
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 mb-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Time Remaining</p>
              <p className="text-2xl font-bold text-blue-700">
                {remainingTime || '--:--'}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Est. Time</p>
            <p className="text-lg font-semibold text-gray-700">
              {order.preparationTime} mins
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // Ready for pickup
  if (order.status === 'Ready') {
    return (
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: [1, 1.02, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
        className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 mb-4 border-2 border-green-300"
      >
        <div className="flex items-center gap-3">
          <Package className="w-8 h-8 text-green-600" />
          <div>
            <p className="text-lg font-bold text-green-700">
              Your order is ready!
            </p>
            <p className="text-sm text-green-600">
              Please collect your order from the counter
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // Pending without time
  if (order.status === 'Pending' && !order.preparationTime) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-3">
          <Clock className="w-6 h-6 text-yellow-600" />
          <div>
            <p className="text-sm font-semibold text-yellow-800">
              Waiting for confirmation
            </p>
            <p className="text-xs text-yellow-600">
              The restaurant will confirm your order shortly
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
});

StatusMessage.displayName = 'StatusMessage';

// ✅ OPTIMIZED: Separate EmptyOrders component
const EmptyOrders = React.memo(({ onBack }) => (
  <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
    <ShoppingBag className="w-20 h-20 text-gray-300 mx-auto mb-4" />
    <h3 className="text-2xl font-bold text-gray-800 mb-2">
      No Active Orders
    </h3>
    <p className="text-gray-600 mb-6">
      You don't have any active orders at the moment
    </p>
    <button
      onClick={onBack}
      className="bg-orange-500 text-white px-8 py-3 rounded-xl hover:bg-orange-600 font-semibold transition-colors shadow-md"
    >
      Browse Menu
    </button>
  </div>
));

EmptyOrders.displayName = 'EmptyOrders';

export default ActiveOrders;